import React from "react";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./Button";
import { PageHeader } from "./PageHeader";

/**
 * Wizard — canonical multi-step-form primitive.
 *
 * Purpose: unlock consistent step-indicator + back/next chrome + validation
 * gating + save-draft support for the four wizards in the app
 * (DisputeLetterWizard, ProposalWizard, Signup, MortgageIntake).
 *
 * Contract:
 *   const steps = [
 *     { key: "profile",   label: "Profile",   render: (ctx) => <ProfileForm .../> },
 *     { key: "docs",      label: "Documents", render: (ctx) => <DocsForm .../> },
 *     { key: "review",    label: "Review",    render: (ctx) => <Review .../> },
 *   ];
 *
 *   <Wizard
 *     steps={steps}
 *     current={idx}
 *     onChange={setIdx}
 *     onFinish={handleSubmit}
 *     canAdvance={validForCurrent}
 *     saveDraftLabel="Save draft"
 *     onSaveDraft={draftSave}
 *   />
 *
 * a11y contract:
 *   * Step indicator is a `<ol>` with `aria-current="step"` on the active row.
 *   * Back/Next controls are DS `Button`s with descriptive labels.
 *   * Live-region status announcement whenever `current` changes.
 */

function StepIndicator({ steps, current }) {
  return (
    <ol
      className="flex items-center gap-3 mb-6 flex-wrap"
      data-testid="wizard-step-indicator"
    >
      {steps.map((step, idx) => {
        const done = idx < current;
        const active = idx === current;
        return (
          <li
            key={step.key || idx}
            aria-current={active ? "step" : undefined}
            className="inline-flex items-center gap-2 text-[13px]"
            data-testid={`wizard-step-${idx}`}
          >
            <span
              className={cn(
                "inline-flex items-center justify-center w-6 h-6 rounded-full border text-[12px] font-semibold",
                done
                  ? "bg-[var(--ds-color-accent)] text-[var(--ds-color-ink-inverse)] border-[var(--ds-color-accent)]"
                  : active
                    ? "bg-[var(--ds-color-surface)] text-[var(--ds-color-ink)] border-[var(--ds-color-accent)]"
                    : "bg-[var(--ds-color-surface)] text-[var(--ds-color-ink-subtle)] border-[var(--ds-color-border)]",
              )}
              aria-hidden
            >
              {done ? <Check className="w-3 h-3" /> : idx + 1}
            </span>
            <span
              className={cn(
                active
                  ? "text-[var(--ds-color-ink)] font-medium"
                  : "text-[var(--ds-color-ink-muted)]",
              )}
            >
              {step.label}
            </span>
            {idx < steps.length - 1 && (
              <span aria-hidden className="mx-1 text-[var(--ds-color-ink-subtle)]">
                /
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export function Wizard({
  steps = [],
  current = 0,
  onChange,
  onFinish,
  onSaveDraft,
  canAdvance = true,
  finishLabel = "Submit",
  saveDraftLabel = "Save draft",
  title,
  description,
  className,
  context,
  "data-testid": testId = "ds-wizard",
}) {
  const clampedIdx = Math.max(0, Math.min(steps.length - 1, current || 0));
  const step = steps[clampedIdx] || {};
  const isLast = clampedIdx === steps.length - 1;

  const goPrev = () => onChange && clampedIdx > 0 && onChange(clampedIdx - 1);
  const goNext = () => {
    if (!canAdvance) return;
    if (isLast) {
      onFinish && onFinish();
    } else {
      onChange && onChange(clampedIdx + 1);
    }
  };

  return (
    <div data-testid={testId} className={cn("space-y-4", className)}>
      {(title || description) && (
        <PageHeader title={title} description={description} data-testid={`${testId}-header`} />
      )}
      <StepIndicator steps={steps} current={clampedIdx} />
      <div
        role="region"
        aria-live="polite"
        aria-label={`Step ${clampedIdx + 1} of ${steps.length}: ${step.label || ""}`}
        data-testid={`${testId}-body`}
      >
        {typeof step.render === "function" ? step.render({ ...(context || {}), current: clampedIdx, isLast }) : null}
      </div>
      <div className="flex items-center justify-between gap-2 pt-4 border-t border-[var(--ds-color-border)]">
        <div>
          {onSaveDraft ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onSaveDraft}
              data-testid={`${testId}-save-draft`}
            >
              {saveDraftLabel}
            </Button>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={goPrev}
            disabled={clampedIdx === 0}
            data-testid={`${testId}-prev`}
          >
            <ChevronLeft className="w-3.5 h-3.5" aria-hidden />
            Back
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={goNext}
            disabled={!canAdvance}
            data-testid={`${testId}-next`}
          >
            {isLast ? finishLabel : "Next"}
            {!isLast && <ChevronRight className="w-3.5 h-3.5" aria-hidden />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default Wizard;
